/*
 * Copyright (C) Motorola Solutions, INC.
 * All Rights Reserved.
 */

import { concatMap, NEVER, Observable, of, race } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

export class PromiseFunctions {

    /**
     * Fires all requests sequentially and returns the first successful request from a list of requests.
     * Preferred because it's more predictable
     * @param requests
     */
    public static sequentialSuccessfulRequests = <T>(requests: Observable<T>[]): Observable<T> => {
        if (requests.length === 0) {
            return new Observable<T>((subscriber) => {
                subscriber.error(new Error('No requests provided'));
            });
        }

        let failedCount = 0;
        const totalCount = requests.length;

        return new Observable<T>((subscriber) => {
            let successFound = false;

            requests.reduce((prev, curr, index) => {
                return prev.pipe(
                    concatMap(() =>
                        curr.pipe(
                            map((result) => {
                                console.log(`Response success [${index}]:`, result);
                                successFound = true;
                                subscriber.next(result);
                                subscriber.complete();
                                return result;
                            }),
                            catchError((err) => {
                                failedCount++;
                                console.log(`Response failed [${index}]:`, failedCount, totalCount, err);
                                if (failedCount === totalCount && !successFound) {
                                    subscriber.error(new Error('All URLs failed'));
                                }
                                return of(null);
                            })
                        )
                    )
                );
            }, of(null)).subscribe();
        });
    };

    /**
     * Fires all requests simultaneously and returns the first successful request from a list of requests.
     * @param requests
     */
    public static raceSuccessfulRequests = <T>(requests: Observable<T>[]): Observable<T> => {

        if (requests.length === 0) {
            return new Observable<T>((subscriber) => {
                subscriber.error(new Error('No requests provided'));
            });
        }

        interface RequestResult {
            result: T;
            index: number;
        }

        let failedCount = 0;
        const totalCount = requests.length;

        const successfulRequests = requests.map((request, index) =>
            request.pipe(
                tap((res) => console.log(`Response success [${index}]:`, res)),
                map((result): RequestResult => ({ result, index })),
                catchError((err) => {
                    failedCount++;
                    console.log(`Response failed [${index}]:`, failedCount, totalCount, err);

                    if (failedCount === totalCount) {
                        return new Observable<RequestResult>((subscriber) => {
                            console.error('All URLS failed');
                            subscriber.error(new Error('All URLs failed'));
                        });
                    }
                    return NEVER as Observable<RequestResult>;
                })
            )
        );

        return race(...successfulRequests).pipe(
            map((res) => res.result)
        );
    };
}
